import { useState, useEffect, useCallback } from 'react';

export interface ColumnConfig {
  field: string;
  visible: boolean;
  order: number;
  width?: number;
}

export interface UseTableColumnsOptions {
  storageKey: string;
  defaultColumns: ColumnConfig[];
}

export const useTableColumns = ({ storageKey, defaultColumns }: UseTableColumnsOptions) => {
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Проверяем, что все колонки из defaultColumns присутствуют
        const savedFields = new Set(parsed.map((c: ColumnConfig) => c.field));
        const defaultFields = new Set(defaultColumns.map((c) => c.field));
        
        // Добавляем отсутствующие колонки из defaultColumns
        const missingColumns = defaultColumns.filter((c) => !savedFields.has(c.field));
        const result = [...parsed, ...missingColumns];
        
        // Удаляем колонки, которых нет в defaultColumns
        const filtered = result.filter((c: ColumnConfig) => defaultFields.has(c.field));
        
        // Обновляем порядок на основе defaultColumns
        const ordered = defaultColumns.map((defaultCol, index) => {
          const saved = filtered.find((c: ColumnConfig) => c.field === defaultCol.field);
          return saved || { ...defaultCol, order: index, width: defaultCol.width };
        });
        
        return ordered.sort((a, b) => a.order - b.order);
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек колонок:', error);
    }
    return defaultColumns;
  });

  const saveColumns = useCallback((newColumns: ColumnConfig[]) => {
    setColumns(newColumns);
    try {
      localStorage.setItem(storageKey, JSON.stringify(newColumns));
    } catch (error) {
      console.error('Ошибка сохранения настроек колонок:', error);
    }
  }, [storageKey]);

  const toggleColumnVisibility = useCallback((field: string) => {
    const newColumns = columns.map((col) =>
      col.field === field ? { ...col, visible: !col.visible } : col
    );
    saveColumns(newColumns);
  }, [columns, saveColumns]);

  const moveColumn = useCallback((field: string, direction: 'up' | 'down') => {
    const newColumns = [...columns];
    const index = newColumns.findIndex((col) => col.field === field);
    
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newColumns.length) return;
    
    // Меняем местами
    [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];
    
    // Обновляем порядок
    const updated = newColumns.map((col, idx) => ({ ...col, order: idx }));
    saveColumns(updated);
  }, [columns, saveColumns]);

  const updateColumnWidth = useCallback((field: string, width: number) => {
    const newColumns = columns.map((col) =>
      col.field === field ? { ...col, width } : col
    );
    saveColumns(newColumns);
  }, [columns, saveColumns]);

  const resetColumns = useCallback(() => {
    saveColumns(defaultColumns);
  }, [defaultColumns, saveColumns]);

  const getVisibleColumns = useCallback(() => {
    return columns.filter((col) => col.visible).sort((a, b) => a.order - b.order);
  }, [columns]);

  const getColumnOrder = useCallback(() => {
    return columns.map((col) => col.field);
  }, [columns]);

  const getColumnWidth = useCallback((field: string): number | undefined => {
    return columns.find((col) => col.field === field)?.width;
  }, [columns]);

  return {
    columns,
    visibleColumns: getVisibleColumns(),
    columnOrder: getColumnOrder(),
    toggleColumnVisibility,
    moveColumn,
    updateColumnWidth,
    resetColumns,
    setColumns: saveColumns,
    getColumnWidth,
  };
};

